/*---------------------------------------------------------------------------------------------
 *  Copyright (c) by Solusek Systems. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React from 'react';
import FormInput from '../components/FormInput';
import { withParamsAndNavigate } from '../components/WithParamsAndNavigate';
import { qreq } from '../shared/qrequest';
import Globals from '../shared/Globals';
import Auth from '../shared/Auth';
import { Link } from 'react-router-dom';
import L from '../components/Lang';
import Icon from '../components/Icon';
import BaseCom from '../components/BaseCom';
import './ClientLogin.css';

class ClientLogin extends BaseCom {
    constructor(props) {
        super(props);

        this.state = {
            credentials: {
                username: '',
                password: ''
            },
            submitting: false,
            loaded: false,
            error: null
        };

        this.submit = this.submit.bind(this);
    }

    async componentDidMount() {
        await this.getMainOrg((j) => {
            this.setState({ instanceError: j.errorMessage });
        });

        try {
            const user = await Auth.getLoginAsync();

            if (user) {
                window.location.assign(Auth.clientFollowPath);
                return;
            }
        }
        catch (e) {
            // Continue to login screen on auth check failure.
        }

        this.setState({ loaded: true });
    }

    submit(e) {
        e.preventDefault();

        if (this.state.submitting)
            return;

        this.setState({ submitting: true, error: null });

        qreq.post(
            '/api/auth/login',
            this.state.credentials,
            (j) => {
                if (j?.errorCode) {
                    this.setState({
                        error: j.errorMessage || 'Invalid username or password.',
                        submitting: false
                    });
                    return;
                }

                if (j?.item) {
                    Globals.set('user', j.item);
                    window.location.assign(Auth.clientFollowPath);
                    return;
                }

                this.setState({
                    error: 'Unexpected login response.',
                    submitting: false
                });
            },
            () => {
                this.setState({
                    error: 'An unknown error occurred. Login service might be down.',
                    submitting: false
                });
            }
        );
    }

    renderLoading() {
        return (
            <section className="client-login-page">
                <div className="client-login-bg" />
                <div className="container client-login-container">
                    <div className="row justify-content-center align-items-center min-vh-100">
                        <div className="col-12 col-sm-10 col-md-6 col-lg-4">
                            <div className="client-login-card client-login-loading">
                                <div className="client-login-icon">
                                    <span className="spinner-border spinner-border-sm" role="status" aria-label="Loading" />
                                </div>
                                <h1 className="client-login-title"><L>Loading</L></h1>
                                <p className="client-login-subtitle"><L>Preparing your secure login...</L></p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    render() {
        if (!this.state.loaded)
            return this.renderLoading();

        return (
            <section className="client-login-page">
                <div className="client-login-bg" />

                <div className="container client-login-container">
                    <div className="client-login-shell">
                        <aside className="client-login-panel">
                            <div className="client-login-brand">
                                <img src="/img/logo-l.png" alt="" />
                            </div>

                            <div className="client-login-panel-content">
                                <span className="client-login-eyebrow"><L>Secure Member Portal</L></span>
                                <h2><L>Welcome back.</L></h2>
                                <p>
                                    <L>Use your email and password to access</L> <strong>{this.state.org?.name}</strong>.
                                </p>
                            </div>

                            <div className="client-login-feature-list">
                                <div>
                                    <Icon icon="faShield" />
                                    <span><L>Protected account access</L></span>
                                </div>
                                <div>
                                    <Icon icon="faLock" />
                                    <span><L>Encrypted session security</L></span>
                                </div>
                                <div>
                                    <Icon icon="faUserCheck" />
                                    <span><L>Member-only portal tools</L></span>
                                </div>
                            </div>
                        </aside>

                        <main className="client-login-card">
                            <div className="client-login-mobile-brand">
                                <img src="/img/logo-l.png" alt="" />
                            </div>

                            <div className="client-login-icon">
                                <Icon icon="faRightToBracket" />
                            </div>

                            <h1 className="client-login-title h1-hero"><L>Login</L></h1>
                            <p className="client-login-subtitle"><L>Continue to your portal.</L></p>

                            {this.state.instanceError ? (
                                <div className="alert alert-danger mb-4" role="alert">
                                    {this.state.instanceError}
                                </div>
                            ) : null}

                            <form onSubmit={this.submit} autoComplete="on" noValidate className="client-login-form">
                                <FormInput
                                    model={this.state.credentials}
                                    name="username"
                                    label="Email Address"
                                    type="text"
                                    required={true}
                                    labelAsPlaceholder={true}
                                    autoComplete="username"
                                />

                                <FormInput
                                    model={this.state.credentials}
                                    name="password"
                                    label="Password"
                                    type="password"
                                    required={true}
                                    labelAsPlaceholder={true}
                                    autoComplete="current-password"
                                />

                                {this.state.error ? (
                                    <div className="alert alert-danger mt-3 mb-2" role="alert">
                                        <L>{this.state.error}</L>
                                    </div>
                                ) : null}

                                <div className="d-grid mt-4">
                                    <button type="submit" className="btn btn-primary btn-lg client-login-submit" disabled={this.state.submitting}>
                                        {this.state.submitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                                <L>Signing in...</L>
                                            </>
                                        ) : (
                                            <>
                                                <Icon icon="faRightToBracket" /> <L>Login</L>
                                            </>
                                        )}
                                    </button>
                                </div>

                                <div className="client-login-links">
                                    <Link to="/client/forgot-password">
                                        <Icon icon="faKey" /> <L>I forgot my password</L>
                                    </Link>
                                </div>
                            </form>
                        </main>
                    </div>
                </div>
            </section>
        );
    }
}

export default withParamsAndNavigate(ClientLogin);
